import React from "react";
import {
  Box,
  Button,
  LinearProgress,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import type { NoticeUploadProgressState } from "../../utils/presignedUpload";
import { formatNoticeUploadStatus } from "../../utils/presignedUpload";

export type NoticeUploadProgressProps = {
  progress: NoticeUploadProgressState | null;
  statusMessage?: string;
  onCancel?: () => void;
  /** Compact overlay for image previews; inline for form steps. */
  variant?: "inline" | "overlay";
  sx?: SxProps<Theme>;
};

export const NoticeUploadProgress: React.FC<NoticeUploadProgressProps> = ({
  progress,
  statusMessage,
  onCancel,
  variant = "inline",
  sx,
}) => {
  if (!progress && !statusMessage) {
    return null;
  }

  const label =
    statusMessage ??
    (progress
      ? formatNoticeUploadStatus(
          progress.fileName,
          progress.percent,
          progress.fileIndex,
          progress.fileCount
        )
      : "Uploading…");

  const percent = progress?.percent ?? 0;

  if (variant === "overlay") {
    return (
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          px: 2,
          backgroundColor: "rgba(0,0,0,0.5)",
          color: "common.white",
          ...sx,
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, textAlign: "center", maxWidth: "100%" }}
        >
          {label}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={percent}
          sx={{
            width: "min(220px, 80%)",
            height: 6,
            borderRadius: 3,
            bgcolor: "rgba(255,255,255,0.25)",
            "& .MuiLinearProgress-bar": { bgcolor: "common.white" },
          }}
        />
        {onCancel ? (
          <Button
            size="small"
            variant="outlined"
            onClick={onCancel}
            sx={{
              color: "common.white",
              borderColor: "rgba(255,255,255,0.7)",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                borderColor: "common.white",
                bgcolor: "rgba(255,255,255,0.12)",
              },
            }}
          >
            Cancel
          </Button>
        ) : null}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "action.hover",
        ...sx,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
        {label}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={percent}
        sx={{ height: 8, borderRadius: 4, mb: onCancel ? 1 : 0 }}
      />
      {onCancel ? (
        <Button
          size="small"
          color="inherit"
          onClick={onCancel}
          sx={{ textTransform: "none", fontWeight: 600, px: 0 }}
        >
          Cancel upload
        </Button>
      ) : null}
    </Box>
  );
};
